/**
 * Custom hook for managing editor undo/redo history
 * 
 * This hook provides a per-document history system that tracks all editor
 * operations including text edits, block splits/merges, reordering, and type changes.
 * 
 * Features:
 * - Per-document history (isolated between tabs)
 * - 50-step limit to control memory usage
 * - Debounced text edit grouping (500ms)
 * - Immediate snapshots for structural changes
 * - Cursor position restoration
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Block } from '@/components/editor/Editor';
import {
  EditorSnapshot,
  createSnapshot,
  areSnapshotsDifferent,
  isStructuralChange,
} from '@/lib/utils/editorHistoryUtils';

interface UseEditorHistoryOptions {
  maxHistorySize?: number;
  debounceMs?: number;
}

interface UseEditorHistoryReturn {
  pushState: (
    blocks: Block[],
    activeBlockId: string,
    cursorPosition: number,
    isStructural?: boolean
  ) => void;
  undo: () => EditorSnapshot | null;
  redo: () => EditorSnapshot | null;
  saveToRedoStack: (snapshot: EditorSnapshot) => void;
  saveToUndoStack: (snapshot: EditorSnapshot) => void;
  canUndo: boolean;
  canRedo: boolean;
  clear: () => void;
}

/**
 * Hook for managing editor history with undo/redo functionality
 */
export const useEditorHistory = (
  options: UseEditorHistoryOptions = {}
): UseEditorHistoryReturn => {
  const {
    maxHistorySize = 50,
    debounceMs = 500,
  } = options;

  // History stacks
  const [undoStack, setUndoStack] = useState<EditorSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditorSnapshot[]>([]);

  // Debounce timer for text edits
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSnapshotRef = useRef<EditorSnapshot | null>(null);

  /**
   * Flush any pending debounced snapshot to the undo stack
   */
  const flushPendingSnapshot = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (pendingSnapshotRef.current) {
      const snapshot = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;

      // Defer state update to avoid React warnings about updating during render
      queueMicrotask(() => {
        setUndoStack(prev => {
          // Check if this snapshot is different from the last one
          const lastSnapshot = prev[prev.length - 1];
          if (!areSnapshotsDifferent(lastSnapshot, snapshot)) {
            return prev;
          }

          const newStack = [...prev, snapshot];
          // Limit stack size
          if (newStack.length > maxHistorySize) {
            return newStack.slice(newStack.length - maxHistorySize);
          }
          return newStack;
        });

        // Clear redo stack when new state is pushed
        setRedoStack([]);
      });
    }
  }, [maxHistorySize]);

  /**
   * Push a new state to the history
   * 
   * @param blocks - Current blocks state
   * @param activeBlockId - ID of the currently focused block
   * @param cursorPosition - Cursor position within the active block
   * @param isStructural - Whether this is a structural change (forces immediate save)
   */
  const pushState = useCallback(
    (
      blocks: Block[],
      activeBlockId: string,
      cursorPosition: number,
      isStructural = false
    ) => {
      const snapshot = createSnapshot(blocks, activeBlockId, cursorPosition);

      // Check if this is a structural change by comparing with last snapshot
      const lastSnapshot = undoStack[undoStack.length - 1] || pendingSnapshotRef.current;
      const isStructuralByContent = isStructuralChange(lastSnapshot, snapshot);

      if (isStructural || isStructuralByContent) {
        // Structural change - flush any pending snapshot first, then add this one immediately
        flushPendingSnapshot();

        // Defer state update to avoid React warnings about updating during render
        queueMicrotask(() => {
          setUndoStack(prev => {
            // Check if this snapshot is different from the last one
            const lastSnapshot = prev[prev.length - 1];
            if (!areSnapshotsDifferent(lastSnapshot, snapshot)) {
              return prev;
            }

            const newStack = [...prev, snapshot];
            // Limit stack size
            if (newStack.length > maxHistorySize) {
              return newStack.slice(newStack.length - maxHistorySize);
            }
            return newStack;
          });

          // Clear redo stack when new state is pushed
          setRedoStack([]);
        });
      } else {
        // Text edit - debounce
        pendingSnapshotRef.current = snapshot;

        // Clear existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
          flushPendingSnapshot();
        }, debounceMs);
      }
    },
    [undoStack, maxHistorySize, debounceMs, flushPendingSnapshot]
  );

  /**
   * Undo the last operation
   * Returns the previous snapshot, or null if no more undo available
   * 
   * Note: The caller should save the current state to redo stack by calling
   * saveCurrentStateToRedo() before restoring the previous snapshot
   */
  const undo = useCallback((): EditorSnapshot | null => {
    // Flush any pending changes first
    flushPendingSnapshot();

    if (undoStack.length === 0) {
      return null;
    }

    // Pop from undo stack
    const previousSnapshot = undoStack[undoStack.length - 1];
    
    setUndoStack(prev => prev.slice(0, -1));
    
    return previousSnapshot;
  }, [undoStack, flushPendingSnapshot]);
  
  /**
   * Save a snapshot to the redo stack
   * This is typically called before performing an undo operation
   */
  const saveToRedoStack = useCallback((snapshot: EditorSnapshot) => {
    setRedoStack(prev => [...prev, snapshot]);
  }, []);

  /**
   * Redo the last undone operation
   * Returns the next snapshot, or null if no more redo available
   * 
   * Note: The caller should save the current state to undo stack by calling
   * saveToUndoStack() before restoring the next snapshot
   */
  const redo = useCallback((): EditorSnapshot | null => {
    if (redoStack.length === 0) {
      return null;
    }

    // Pop from redo stack
    const nextSnapshot = redoStack[redoStack.length - 1];
    
    setRedoStack(prev => prev.slice(0, -1));
    
    return nextSnapshot;
  }, [redoStack]);
  
  /**
   * Save a snapshot to the undo stack
   * This is typically called before performing a redo operation
   */
  const saveToUndoStack = useCallback((snapshot: EditorSnapshot) => {
    setUndoStack(prev => {
      const newStack = [...prev, snapshot];
      // Limit stack size
      if (newStack.length > maxHistorySize) {
        return newStack.slice(newStack.length - maxHistorySize);
      }
      return newStack;
    });
  }, [maxHistorySize]);

  /**
   * Clear all history (useful when switching documents)
   */
  const clear = useCallback(() => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingSnapshotRef.current = null;
    
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    pushState,
    undo,
    redo,
    saveToRedoStack,
    saveToUndoStack,
    canUndo: undoStack.length > 0 || pendingSnapshotRef.current !== null,
    canRedo: redoStack.length > 0,
    clear,
  };
};
